import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ItemCard, ItemCardCategory, ItemCardSet, ItemCardUserImage } from '../../types/pointItemNote';
import type {
  AdminProfileCardSlot,
  ProfileCardSetSource,
  ProfileCardSetView,
} from '../../types/profileCard';
import { profileCardSetKey } from '../../types/profileCard';
import ProfileCardCachedImage from '../common/ProfileCardCachedImage';
import {
  getAdminProfileCardSets,
  getWorkspaceProfileCardSettings,
  saveWorkspaceProfileCardSettings,
} from '../../services/profileCardCatalogService';
import {
  getWorkspaceProfileCardSets,
  saveWorkspaceProfileCardSetSettings,
} from '../../services/workspaceProfileCardService';
import {
  loadAdminProfileCardSetsCache,
  loadWorkspaceProfileCardSettingsCache,
  loadWorkspaceProfileCardSetsCache,
} from '../../utils/profileCardCatalogCache';
import { resolveItemCardImageSrc } from '../../utils/itemCardImage';
import ProfileCardManageModal, {
  type ProfileCardManageTarget,
} from './ProfileCardManageModal';
import ItemCardImageSelectModal from './ItemCardImageSelectModal';
import ItemCardStackIcon from './ItemCardStackIcon';
import UnsavedCloseConfirm from './UnsavedCloseConfirm';
import { MAX_WORKSPACE_PROFILE_CARD_SETS } from '../../types/profileCard';

type TabKey = Exclude<ItemCardCategory, 'classPoint'> | 'profile';

type Props = {
  open: boolean;
  itemCards: ItemCardSet;
  itemCardImages?: ItemCardUserImage[];
  workspaceId: string;
  onClose: () => void;
  onSave: (itemCards: ItemCardSet) => void;
  onItemCardImagesChange: (
    images: ItemCardUserImage[],
    options?: { silent?: boolean }
  ) => void;
};

const TAB_LABELS: Record<TabKey, string> = {
  levelup: '레벨업 아이템',
  assignment: '과제보상 아이템',
  store: '스토어 아이템',
  profile: '프로필 카드',
};

type ProfileCardDraftSetting = {
  source: ProfileCardSetSource;
  enabled: boolean;
  /** 편집 중 빈 칸 허용(레벨업 포인트 입력과 동일) */
  priceCoins: number | '';
};
type ProfileCardDraftMap = Record<string, ProfileCardDraftSetting>;

function newId(): string {
  return `ic-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const ProfileSetCover: React.FC<{
  source: ProfileCardSetSource;
  cover?: AdminProfileCardSlot;
}> = ({ source, cover }) => {
  if (!cover?.storagePath) {
    return <span className="cp-pin-profile-set-thumb-ph">?</span>;
  }
  return (
    <ProfileCardCachedImage
      source={source}
      storagePath={cover.storagePath}
      imageVersion={cover.imageVersion}
      alt=""
    />
  );
};

function listForTab(set: ItemCardSet, tab: ItemCardCategory | 'profile'): ItemCard[] {
  if (tab === 'levelup') return set.levelupItem;
  if (tab === 'assignment') return set.assignmentItem;
  if (tab === 'store') return set.storeItem;
  return [];
}

function withTabList(
  set: ItemCardSet,
  tab: ItemCardCategory | 'profile',
  list: ItemCard[]
): ItemCardSet {
  if (tab === 'levelup') return { ...set, levelupItem: list };
  if (tab === 'assignment') return { ...set, assignmentItem: list };
  if (tab === 'store') return { ...set, storeItem: list };
  return set;
}

function cloneCard(item: ItemCard): ItemCard {
  return { ...item };
}

const CoinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="10" fill="#f39c12" />
    <circle cx="12" cy="12" r="8" fill="#f1c40f" />
    <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">
      ₩
    </text>
  </svg>
);

const HeartIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z"
      fill="#ff4757"
    />
  </svg>
);

const ItemCardSettingsModal: React.FC<Props> = ({
  open,
  itemCards,
  itemCardImages = [],
  workspaceId,
  onClose,
  onSave,
  onItemCardImagesChange,
}) => {
  const [tab, setTab] = useState<TabKey>('levelup');
  const [draft, setDraft] = useState<ItemCardSet>(itemCards);
  const [editing, setEditing] = useState<ItemCard | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [editSnapshot, setEditSnapshot] = useState<ItemCard | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; category: ItemCardCategory } | null>(
    null
  );
  const [confirmClose, setConfirmClose] = useState(false);
  const [cardImageModalOpen, setCardImageModalOpen] = useState(false);

  const [profileSets, setProfileSets] = useState<ProfileCardSetView[]>([]);
  const [profileBaseline, setProfileBaseline] = useState<ProfileCardDraftMap>({});
  const [profileDraft, setProfileDraft] = useState<ProfileCardDraftMap>({});
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileManageTarget, setProfileManageTarget] = useState<ProfileCardManageTarget | null>(
    null
  );

  const applyProfileSetViews = useCallback(
    (
      adminSets: Awaited<ReturnType<typeof getAdminProfileCardSets>>,
      adminSettings: Awaited<ReturnType<typeof getWorkspaceProfileCardSettings>>,
      userSets: Awaited<ReturnType<typeof getWorkspaceProfileCardSets>>,
      opts?: { syncDraft?: boolean }
    ) => {
      const views: ProfileCardSetView[] = [
        ...adminSets.map((s) => {
          const found = adminSettings.find((x) => x.setId === s.id);
          return {
            id: s.id,
            name: s.name,
            sortOrder: s.sortOrder,
            source: 'admin' as const,
            enabled: found?.enabled ?? false,
            priceCoins: found?.priceCoins ?? 100,
            slots: s.slots,
            createdAt: s.createdAt,
            updatedAt: s.updatedAt,
          };
        }),
        ...userSets.map((s) => ({
          id: s.id,
          name: s.name,
          sortOrder: s.sortOrder,
          source: 'workspace' as const,
          enabled: s.enabled,
          priceCoins: s.priceCoins ?? 100,
          slots: s.slots,
          createdAt: s.createdAt,
          updatedAt: s.updatedAt,
        })),
      ];
      setProfileSets(views);
      if (opts?.syncDraft !== false) {
        const map: ProfileCardDraftMap = {};
        views.forEach((s) => {
          map[profileCardSetKey(s.source, s.id)] = {
            source: s.source,
            enabled: s.enabled,
            priceCoins: s.priceCoins,
          };
        });
        setProfileBaseline(map);
        setProfileDraft(map);
      }
    },
    []
  );

  const reloadProfileSets = useCallback(async () => {
    if (!workspaceId) return;
    setProfileError('');
    const cachedAdmin = loadAdminProfileCardSetsCache();
    const cachedSettings = loadWorkspaceProfileCardSettingsCache(workspaceId);
    const cachedUser = loadWorkspaceProfileCardSetsCache(workspaceId);
    // 세 조각이 모두 있을 때만 즉시 표시(일부만 있으면 잘못된 병합을 피함)
    if (cachedAdmin && cachedSettings && cachedUser) {
      applyProfileSetViews(cachedAdmin.sets, cachedSettings.settings, cachedUser.sets);
    } else {
      setProfileLoading(true);
    }
    try {
      const [adminSets, adminSettings, userSets] = await Promise.all([
        getAdminProfileCardSets(),
        getWorkspaceProfileCardSettings(workspaceId),
        getWorkspaceProfileCardSets(workspaceId),
      ]);
      applyProfileSetViews(adminSets, adminSettings, userSets);
    } catch (e) {
      setProfileError(e instanceof Error ? e.message : '세트를 불러오지 못했습니다.');
    } finally {
      setProfileLoading(false);
    }
  }, [workspaceId, applyProfileSetViews]);

  useEffect(() => {
    if (!open) return;
    setTab('levelup');
    setDraft({
      levelupItem: itemCards.levelupItem.map(cloneCard),
      assignmentItem: itemCards.assignmentItem.map(cloneCard),
      storeItem: itemCards.storeItem.map(cloneCard),
    });
    setEditing(null);
    setIsNew(false);
    setEditSnapshot(null);
    setDeleteTarget(null);
    setConfirmClose(false);
    setCardImageModalOpen(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // 외부 itemCards가 바뀐 경우에만 동기화.
    // editing을 deps에 넣으면 확인(편집 종료) 직후 부모 미반영 draft가 덮여 새 아이템이 사라짐.
    if (editing != null || cardImageModalOpen) return;
    setDraft({
      levelupItem: itemCards.levelupItem.map(cloneCard),
      assignmentItem: itemCards.assignmentItem.map(cloneCard),
      storeItem: itemCards.storeItem.map(cloneCard),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- editing/cardImageModalOpen은 가드만, 종료 시 동기화하면 안 됨
  }, [itemCards, open]);

  useEffect(() => {
    if (!open || !workspaceId) return;
    void reloadProfileSets();
  }, [open, workspaceId, reloadProfileSets]);

  useEffect(() => {
    if (!open) setProfileManageTarget(null);
  }, [open]);

  const userProfileSetCount = useMemo(
    () => profileSets.filter((s) => s.source === 'workspace').length,
    [profileSets]
  );
  const userProfileSetLimitReached = userProfileSetCount >= MAX_WORKSPACE_PROFILE_CARD_SETS;

  const baseline = useMemo(() => JSON.stringify(itemCards), [itemCards]);
  const draftSnapshot = useMemo(() => JSON.stringify(draft), [draft]);
  const profileBaselineSnapshot = useMemo(() => JSON.stringify(profileBaseline), [profileBaseline]);
  const profileDraftSnapshot = useMemo(() => JSON.stringify(profileDraft), [profileDraft]);
  const dirty =
    open &&
    (baseline !== draftSnapshot || editing != null || profileBaselineSnapshot !== profileDraftSnapshot);

  if (!open) return null;

  const current = tab === 'profile' ? [] : listForTab(draft, tab);

  const handleClose = () => {
    if (dirty) setConfirmClose(true);
    else onClose();
  };

  const startEdit = (item: ItemCard) => {
    if (editing?.itemCardId === item.itemCardId) return;
    if (editing && isNew) {
      // 새 아이템 편집 중 다른 카드 클릭 → 새 아이템 제거 후 전환
      setDraft((prev) =>
        withTabList(
          prev,
          editing.category,
          listForTab(prev, editing.category).filter((x) => x.itemCardId !== editing.itemCardId)
        )
      );
    }
    setEditing(cloneCard(item));
    setEditSnapshot(cloneCard(item));
    setIsNew(false);
  };

  const handleAdd = () => {
    if (editing || tab === 'profile') return;
    const now = new Date().toISOString();
    const item: ItemCard = {
      itemCardId: newId(),
      cost: tab === 'store' ? 0 : 'Nocharge',
      type: 'passive',
      cardImage: 'default',
      name: '',
      memo: '',
      category: tab,
      createdAt: now,
      updatedAt: now,
    };
    setDraft((prev) => withTabList(prev, tab, [...listForTab(prev, tab), item]));
    setEditing(cloneCard(item));
    setEditSnapshot(null);
    setIsNew(true);
  };

  const confirmEdit = () => {
    if (!editing) return;
    const name = editing.name.trim();
    if (!name) return;
    const now = new Date().toISOString();
    const nextItem: ItemCard = {
      ...editing,
      name,
      memo: editing.memo.trim(),
      updatedAt: now,
      useCount: editing.type === 'active' ? Math.max(1, editing.useCount ?? 1) : undefined,
    };
    setDraft((prev) =>
      withTabList(
        prev,
        nextItem.category,
        listForTab(prev, nextItem.category).map((x) =>
          x.itemCardId === nextItem.itemCardId ? nextItem : x
        )
      )
    );
    setEditing(null);
    setIsNew(false);
    setEditSnapshot(null);
  };

  const cancelEdit = () => {
    if (!editing) return;
    if (isNew) {
      setDraft((prev) =>
        withTabList(
          prev,
          editing.category,
          listForTab(prev, editing.category).filter((x) => x.itemCardId !== editing.itemCardId)
        )
      );
    } else if (editSnapshot) {
      setDraft((prev) =>
        withTabList(
          prev,
          editing.category,
          listForTab(prev, editing.category).map((x) =>
            x.itemCardId === editing.itemCardId ? cloneCard(editSnapshot) : x
          )
        )
      );
    }
    setEditing(null);
    setIsNew(false);
    setEditSnapshot(null);
  };

  const requestDelete = (item: ItemCard) => {
    setDeleteTarget({ id: item.itemCardId, category: item.category });
  };

  const executeDelete = () => {
    if (!deleteTarget) return;
    setDraft((prev) =>
      withTabList(
        prev,
        deleteTarget.category,
        listForTab(prev, deleteTarget.category).filter((x) => x.itemCardId !== deleteTarget.id)
      )
    );
    if (editing?.itemCardId === deleteTarget.id) {
      setEditing(null);
      setIsNew(false);
      setEditSnapshot(null);
    }
    setDeleteTarget(null);
  };

  const handleSave = async () => {
    let next = draft;
    if (editing) {
      const name = editing.name.trim();
      if (!name) return;
      const now = new Date().toISOString();
      const nextItem: ItemCard = {
        ...editing,
        name,
        memo: editing.memo.trim(),
        updatedAt: now,
        useCount: editing.type === 'active' ? Math.max(1, editing.useCount ?? 1) : undefined,
      };
      next = withTabList(
        draft,
        nextItem.category,
        listForTab(draft, nextItem.category).map((x) =>
          x.itemCardId === nextItem.itemCardId ? nextItem : x
        )
      );
    }
    if (profileBaselineSnapshot !== profileDraftSnapshot && workspaceId) {
      setProfileSaving(true);
      setProfileError('');
      try {
        const adminEntries = Object.entries(profileDraft).filter(([, v]) => v.source === 'admin');
        const userEntries = Object.entries(profileDraft).filter(([, v]) => v.source === 'workspace');
        await Promise.all([
          saveWorkspaceProfileCardSettings(
            workspaceId,
            adminEntries.map(([key, v]) => ({
              setId: key.slice('admin:'.length),
              enabled: v.enabled,
              priceCoins: typeof v.priceCoins === 'number' ? v.priceCoins : 0,
            }))
          ),
          saveWorkspaceProfileCardSetSettings(
            userEntries.map(([key, v]) => ({
              setId: key.slice('workspace:'.length),
              enabled: v.enabled,
              priceCoins: typeof v.priceCoins === 'number' ? v.priceCoins : 0,
            }))
          ),
        ]);
      } catch (e) {
        setProfileSaving(false);
        setProfileError(e instanceof Error ? e.message : '프로필 카드 설정 저장에 실패했습니다.');
        return;
      }
      setProfileSaving(false);
    }
    onSave(next);
    onClose();
  };

  const toggleType = () => {
    if (!editing) return;
    const nextType = editing.type === 'passive' ? 'active' : 'passive';
    setEditing({
      ...editing,
      type: nextType,
      useCount: nextType === 'active' ? editing.useCount || 1 : undefined,
    });
  };

  return createPortal(
    <div className="cp-apn-modal-overlay" role="presentation" onClick={handleClose}>
      <div
        className="cp-apn-modal cp-pin-settings-modal cp-pin-settings-modal--wide cp-pin-item-modal"
        data-tutorial-pin="item-card-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-pin-item-card-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cp-pin-modal-header">
          <h2 id="cp-pin-item-card-title">아이템 카드 설정</h2>
          <button type="button" className="cp-pin-modal-close" onClick={handleClose} aria-label="닫기">
            ×
          </button>
        </header>

        <div className="cp-pin-bar-tabs cp-pin-bar-tabs--4" role="tablist" data-tutorial-pin="item-card-tabs">
          {(Object.keys(TAB_LABELS) as TabKey[]).map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={`cp-pin-bar-tab${tab === key ? ' is-active' : ''}`}
              onClick={() => {
                if (editing) return;
                setTab(key);
              }}
            >
              {TAB_LABELS[key]}
            </button>
          ))}
        </div>

        <div className="cp-pin-modal-body cp-pin-item-modal-body" data-tutorial-pin="item-card-body">
          {tab === 'profile' ? (
            <div className="cp-pin-profile-tab" data-tutorial-pin="item-card-profile-body">
              {profileError ? <p className="cp-pin-profile-error">{profileError}</p> : null}
              {profileLoading ? (
                <p className="cp-pin-profile-empty">불러오는 중…</p>
              ) : (
                <div className="cp-pin-profile-set-grid">
                  {profileSets.map((set, setIndex) => {
                    const key = profileCardSetKey(set.source, set.id);
                    const cover = [...set.slots].sort(
                      (a, b) => a.slotIndex - b.slotIndex
                    )[0];
                    const value = profileDraft[key] ?? {
                      source: set.source,
                      enabled: false,
                      priceCoins: 100,
                    };
                    const isTutorialSample = setIndex === 0;
                    return (
                      <div key={key} className="cp-pin-profile-set-stack">
                        <div
                          className={`cp-pin-profile-set-card${value.enabled ? ' is-enabled' : ''}`}
                          role="button"
                          tabIndex={0}
                          data-tutorial-pin={
                            set.source === 'admin' ? 'profile-set-admin' : 'profile-set-workspace'
                          }
                          onClick={() =>
                            setProfileManageTarget({
                              mode: 'edit',
                              source: set.source,
                              setId: set.id,
                              name: set.name,
                              slots: set.slots,
                              priceCoins:
                                typeof value.priceCoins === 'number' ? value.priceCoins : 0,
                            })
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setProfileManageTarget({
                                mode: 'edit',
                                source: set.source,
                                setId: set.id,
                                name: set.name,
                                slots: set.slots,
                                priceCoins:
                                  typeof value.priceCoins === 'number' ? value.priceCoins : 0,
                              });
                            }
                          }}
                        >
                          <span className="cp-pin-profile-set-hang" aria-hidden />
                          <div className="cp-pin-profile-set-head">
                            <div className="cp-pin-profile-set-name">
                              <span
                                className={`cp-admin-learning-template-dot ${
                                  set.source === 'admin'
                                    ? 'cp-admin-learning-template-dot--default'
                                    : 'cp-admin-learning-template-dot--user'
                                }`}
                                aria-hidden
                              />
                              <span>{set.name}</span>
                            </div>
                          </div>
                          <div className="cp-pin-profile-set-thumb">
                            <ProfileSetCover source={set.source} cover={cover} />
                          </div>
                          <div className="cp-pin-profile-set-foot">
                            <button
                              type="button"
                              className="cp-pin-profile-set-toggle"
                              aria-pressed={value.enabled}
                              data-tutorial-pin={isTutorialSample ? 'profile-set-toggle' : undefined}
                              onClick={(e) => {
                                e.stopPropagation();
                                setProfileDraft((prev) => ({
                                  ...prev,
                                  [key]: { ...value, enabled: !value.enabled },
                                }));
                              }}
                            >
                              {value.enabled ? '사용 중' : '미사용'}
                            </button>
                            <label
                              className="cp-pin-profile-set-price"
                              data-tutorial-pin={isTutorialSample ? 'profile-set-price' : undefined}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <CoinIcon />
                              <input
                                type="text"
                                inputMode="numeric"
                                aria-label="세트 코인"
                                value={value.priceCoins}
                                onChange={(e) => {
                                  const raw = e.target.value.trim();
                                  if (raw === '') {
                                    setProfileDraft((prev) => ({
                                      ...prev,
                                      [key]: { ...value, priceCoins: '' },
                                    }));
                                    return;
                                  }
                                  if (!/^\d+$/.test(raw)) return;
                                  setProfileDraft((prev) => ({
                                    ...prev,
                                    [key]: {
                                      ...value,
                                      priceCoins: Math.max(0, parseInt(raw, 10)),
                                    },
                                  }));
                                }}
                                onBlur={() => {
                                  if (value.priceCoins === '' || value.priceCoins === undefined) {
                                    setProfileDraft((prev) => ({
                                      ...prev,
                                      [key]: { ...value, priceCoins: 0 },
                                    }));
                                  }
                                }}
                                onFocus={(e) => e.target.select()}
                              />
                              <span>코인</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    className={`cp-pin-item-add-card cp-pin-profile-set-add${
                      userProfileSetLimitReached ? ' is-limit' : ''
                    }`}
                    data-tutorial-pin="profile-card-set-add"
                    disabled={userProfileSetLimitReached}
                    onClick={() => {
                      if (userProfileSetLimitReached) return;
                      setProfileManageTarget({ mode: 'create' });
                    }}
                  >
                    {userProfileSetLimitReached ? (
                      <span className="cp-pin-profile-add-limit-msg">
                        사용자 세트 2개를 모두 생성하여 더이상 세트를 생성할 수 없습니다.
                      </span>
                    ) : (
                      <>
                        <span className="cp-pin-item-add-circle" aria-hidden>
                          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                            <circle
                              cx="24"
                              cy="24"
                              r="23"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeDasharray="4 4"
                              fill="none"
                            />
                            <path
                              d="M24 14V34M14 24H34"
                              stroke="currentColor"
                              strokeWidth="3"
                              strokeLinecap="round"
                            />
                          </svg>
                        </span>
                        <span className="cp-pin-profile-add-label">
                          <span>프로필 카드 세트</span>
                          <span>추가</span>
                        </span>
                      </>
                    )}
                  </button>
                </div>
              )}
              <p className="cp-pin-profile-hint">
                멤버가 프로필 카드를 구입할 때 세트별로 차감되는 코인입니다. 카드를 클릭하면 세트 내용을
                확인할 수 있고, 「프로필 카드 세트 추가」로 사용자 세트를 만들 수 있습니다.
              </p>
            </div>
          ) : (
          <div className="cp-pin-item-grid">
            {current.map((item) => {
              const isEditing = editing?.itemCardId === item.itemCardId;
              return (
                <div
                  key={item.itemCardId}
                  className={`cp-pin-item-card${isEditing ? ' is-editing' : ''}`}
                  onClick={() => {
                    if (!isEditing) startEdit(item);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (!isEditing && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      startEdit(item);
                    }
                  }}
                >
                  {isEditing && editing ? (
                    <div className="cp-pin-item-edit-form" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="cp-pin-item-image-btn"
                        title="카드 이미지 선택"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCardImageModalOpen(true);
                        }}
                      >
                        {(() => {
                          const src = resolveItemCardImageSrc(editing.cardImage);
                          return src ? (
                            <img src={src} alt="" />
                          ) : (
                            <span className="cp-pin-item-image-placeholder">
                              <ItemCardStackIcon size={40} />
                            </span>
                          );
                        })()}
                      </button>

                      <div className="cp-pin-item-type-toggle-wrap">
                        <button
                          type="button"
                          className={`cp-pin-item-type-toggle${editing.type === 'active' ? ' is-active' : ''}`}
                          data-tutorial-pin="item-card-type-toggle"
                          onClick={toggleType}
                          aria-label="패시브/액티브 전환"
                        >
                          <span className={editing.type === 'passive' ? 'is-on' : ''}>패시브</span>
                          <span className={editing.type === 'active' ? 'is-on' : ''}>액티브</span>
                        </button>
                      </div>

                      <label className="cp-pin-item-field">
                        <input
                          type="text"
                          value={editing.name}
                          placeholder="아이템 이름"
                          aria-label="아이템 이름"
                          onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                        />
                      </label>

                      {editing.type === 'active' ? (
                        <label className="cp-pin-item-field" data-tutorial-pin="item-card-use-count">
                          <input
                            type="number"
                            min={1}
                            value={editing.useCount ?? 1}
                            placeholder="사용 횟수"
                            aria-label="사용 횟수"
                            onChange={(e) =>
                              setEditing({
                                ...editing,
                                useCount: Math.max(1, Number(e.target.value) || 1),
                              })
                            }
                          />
                        </label>
                      ) : null}

                      {tab === 'store' ? (
                        <label className="cp-pin-item-cost-field cp-pin-profile-set-price">
                          <CoinIcon />
                          <input
                            type="text"
                            inputMode="numeric"
                            value={editing.cost === 'Nocharge' ? 0 : editing.cost}
                            aria-label="코인"
                            onChange={(e) => {
                              const raw = e.target.value.trim();
                              if (raw === '') {
                                setEditing({ ...editing, cost: 0 });
                                return;
                              }
                              if (!/^\d+$/.test(raw)) return;
                              setEditing({ ...editing, cost: Math.max(0, parseInt(raw, 10)) });
                            }}
                            onFocus={(e) => e.target.select()}
                          />
                          <span>코인</span>
                        </label>
                      ) : null}

                      <label className="cp-pin-item-field cp-pin-item-field--grow">
                        <textarea
                          rows={2}
                          value={editing.memo}
                          placeholder="아이템 설명"
                          aria-label="아이템 설명"
                          onChange={(e) => setEditing({ ...editing, memo: e.target.value })}
                        />
                      </label>

                      <div className="cp-pin-item-card-btns" data-tutorial-pin="item-card-edit-actions">
                        <button
                          type="button"
                          className="cp-pin-item-btn cp-pin-item-btn--cancel"
                          data-tutorial-pin="item-card-cancel"
                          onClick={cancelEdit}
                        >
                          취소
                        </button>
                        <button
                          type="button"
                          className="cp-pin-item-btn cp-pin-item-btn--ok"
                          data-tutorial-pin="item-card-confirm"
                          disabled={!editing.name.trim()}
                          onClick={confirmEdit}
                        >
                          확인
                        </button>
                        <button
                          type="button"
                          className="cp-pin-item-btn cp-pin-item-btn--del"
                          onClick={() => requestDelete(item)}
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="cp-pin-item-icon-area" aria-hidden>
                        {(() => {
                          const src = resolveItemCardImageSrc(item.cardImage);
                          if (src) {
                            return <img src={src} alt="" />;
                          }
                          return (
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                              <rect
                                x="2"
                                y="6"
                                width="12"
                                height="16"
                                rx="2"
                                fill="#ffffff"
                                stroke="#555"
                                strokeWidth="1.5"
                                transform="rotate(-10 8 14)"
                              />
                              <rect
                                x="6"
                                y="4"
                                width="12"
                                height="16"
                                rx="2"
                                fill="#f8f8f8"
                                stroke="#555"
                                strokeWidth="1.5"
                              />
                              <rect
                                x="10"
                                y="6"
                                width="12"
                                height="16"
                                rx="2"
                                fill="#fafafa"
                                stroke="#555"
                                strokeWidth="1.5"
                                transform="rotate(10 16 14)"
                              />
                            </svg>
                          );
                        })()}
                      </div>
                      <div className="cp-pin-item-name">{item.name || '이름 없음'}</div>
                      <div className="cp-pin-item-memo">{item.memo || ''}</div>
                      {(item.category === 'store' && item.cost !== 'Nocharge') ||
                      (item.type === 'active' && item.useCount != null) ? (
                        <div className="cp-pin-item-meta">
                          {item.category === 'store' && item.cost !== 'Nocharge' ? (
                            <span className="cp-pin-item-cost">
                              <CoinIcon />
                              <span>X{item.cost}</span>
                            </span>
                          ) : null}
                          {item.type === 'active' && item.useCount != null ? (
                            <span className="cp-pin-item-uses">
                              <HeartIcon />
                              <span>X{item.useCount}</span>
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              );
            })}

            <button
              type="button"
              className="cp-pin-item-add-card"
              data-tutorial-pin="item-card-add"
              disabled={editing != null}
              onClick={handleAdd}
            >
              <span className="cp-pin-item-add-circle" aria-hidden>
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <circle
                    cx="24"
                    cy="24"
                    r="23"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeDasharray="4 4"
                    fill="none"
                  />
                  <path
                    d="M24 14V34M14 24H34"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <span>아이템 추가</span>
            </button>
          </div>
          )}
        </div>

        <footer className="cp-pin-modal-footer" data-tutorial-pin="item-card-footer">
          <button type="button" className="cp-pin-modal-btn cp-pin-modal-btn--secondary" onClick={handleClose}>
            닫기
          </button>
          <button
            type="button"
            className="cp-pin-modal-btn cp-pin-modal-btn--primary"
            data-tutorial-pin="item-card-save"
            disabled={profileSaving}
            onClick={() => void handleSave()}
          >
            {profileSaving ? '저장 중…' : '저장'}
          </button>
        </footer>

        {deleteTarget ? (
          <div
            className="cp-pin-confirm-overlay"
            role="presentation"
            onClick={() => setDeleteTarget(null)}
          >
            <div className="cp-pin-confirm" role="dialog" onClick={(e) => e.stopPropagation()}>
              <p>이 아이템 카드를 삭제하시겠습니까?</p>
              <div className="cp-pin-confirm-actions">
                <button
                  type="button"
                  className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
                  onClick={() => setDeleteTarget(null)}
                >
                  취소
                </button>
                <button type="button" className="cp-apn-btn cp-apn-btn-danger" onClick={executeDelete}>
                  삭제
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <UnsavedCloseConfirm
          open={confirmClose}
          onCancel={() => setConfirmClose(false)}
          onConfirm={() => {
            setConfirmClose(false);
            onClose();
          }}
        />

        <ProfileCardManageModal
          open={profileManageTarget != null}
          workspaceId={workspaceId}
          target={profileManageTarget}
          existingUserSetCount={userProfileSetCount}
          onClose={() => setProfileManageTarget(null)}
          onSaved={() => {
            void reloadProfileSets();
          }}
        />

        <ItemCardImageSelectModal
          open={cardImageModalOpen && editing != null}
          currentCardImage={editing?.cardImage}
          userImages={itemCardImages}
          onClose={() => setCardImageModalOpen(false)}
          onApply={(cardImage) => {
            if (!editing) return;
            setEditing({ ...editing, cardImage });
          }}
          onUserImagesChange={(images) => onItemCardImagesChange(images, { silent: true })}
        />
      </div>
    </div>,
    document.body
  );
};

export default ItemCardSettingsModal;
